import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_TAGS } from '@/shared/api';
import { settingsKeys } from '@/entities/settings';
import { fetchThemeParameters, fetchThemeValues, createTheme, updateTheme, deleteTheme } from './api';
import type { CreateThemeRequest, UpdateThemeRequest } from './types';

export const themesKeys = {
  all: [API_TAGS.THEMES] as const,
  parameters: () => [...themesKeys.all, 'parameters'] as const,
  values: () => [...themesKeys.all, 'values'] as const,
};

/**
 * Hook for fetching theme parameter structure
 */
export const useThemeParameters = () => {
  return useQuery({
    queryKey: themesKeys.parameters(),
    queryFn: fetchThemeParameters,
  });
};

/**
 * Hook for fetching theme values
 */
export const useThemeValues = () => {
  return useQuery({
    queryKey: themesKeys.values(),
    queryFn: fetchThemeValues,
  });
};

/**
 * Hook for creating a new theme
 */
export const useCreateTheme = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: CreateThemeRequest) => createTheme(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: themesKeys.values() });
      queryClient.invalidateQueries({ queryKey: settingsKeys.themeConfig() });
    },
  });
};

/**
 * Hook for updating an existing theme
 */
export const useUpdateTheme = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: UpdateThemeRequest }) => updateTheme(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: themesKeys.values() });
      queryClient.invalidateQueries({ queryKey: settingsKeys.themeConfig() });
    },
  });
};

/**
 * Hook for deleting a theme
 */
export const useDeleteTheme = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => deleteTheme(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: themesKeys.values() });
      queryClient.invalidateQueries({ queryKey: settingsKeys.themeConfig() });
    },
  });
};
